import { computed } from '@preact/signals';
import {
  ARMOR_BY_ID,
  BANNER_BY_ID,
  CAMO_BY_ID,
  contentItem as catalogContentItem,
  DEATH_FX_BY_ID,
  GADGET_BY_ID,
  MAP_LIST,
  MODE_INFO,
  PET_BY_ID,
  SHOP_WEAPONS,
  SKIN_PRESET_BY_ID,
  STARTER_LOADOUT,
  SCOPE_BY_ID,
  TRACER_BY_ID,
  WEAPON_BY_ID,
  weaponTable,
  type MapDef,
  type ModeInfo,
} from '@vm/content';
import type { Currency, ItemType, ModeId, PetDef, PetRole, Rarity, RewardBundle, WeaponDef } from '@vm/shared';
import { catalog, catalogEntry, level, loadout, owns } from './store';

// Modes & maps ---------------------------------------------------------------------------------------

/** Maps playable in the mode right now (live-ops can switch maps off; the catalog lists them). */
export function mapsForMode(mode: ModeId): MapDef[] {
  const info = MODE_INFO[mode];
  const off = catalog.value?.mapsOff?.[mode] ?? [];
  return MAP_LIST.filter((m) => (m.modes.includes(mode) || info?.maps.includes(m.id)) && !off.includes(m.id));
}

export function mapName(id: string): string {
  return MAP_LIST.find((m) => m.id === id)?.name ?? id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Modes shown in the online play menu: listed, enabled by live-ops, and with at least one map. */
export const onlineModes = computed<ModeInfo[]>(() => {
  const enabled = catalog.value?.modes;
  return Object.values(MODE_INFO).filter((m) => m.listed && (!enabled || enabled.includes(m.id)));
});

/** Modes that can run offline against bots (need a map). */
export const practiceModes = computed<ModeInfo[]>(() => {
  const ids: ModeId[] = ['practice', ...Object.values(MODE_INFO).filter((m) => m.listed).map((m) => m.id)];
  return ids.map((id) => MODE_INFO[id]).filter((m) => m && mapsForMode(m.id).length > 0);
});

export function modeName(mode: ModeId | string): string {
  return MODE_INFO[mode as ModeId]?.name ?? mode;
}

// Live weapon balance --------------------------------------------------------------------------------

/** Weapons with the live balance from the catalog: the armory shows these stats and offline practice plays with them. */
export const liveWeapons = computed(() => weaponTable(catalog.value?.weapons));

/** Taken out of play by live-ops: can't be bought, and loadouts spawn with another weapon of the slot. */
export function weaponOff(id: string): boolean {
  return liveWeapons.value.disabled.has(id);
}

// Items ------------------------------------------------------------------------------------------------

export const RARITY_ORDER: Rarity[] = ['common', 'rare', 'epic', 'legendary', 'mythic'];
export const RARITY_LABEL: Record<Rarity, string> = { common: 'Common', rare: 'Rare', epic: 'Epic', legendary: 'Legendary', mythic: 'Mythic' };

export const TYPE_LABEL: Record<ItemType, string> = {
  weapon: 'Weapon',
  gadget: 'Gadget',
  skin: 'Skin',
  armor: 'Armor',
  camo: 'Camo',
  tracer: 'Tracer',
  scope: 'Scope',
  deathfx: 'Death Effect',
  banner: 'Banner',
  title: 'Title',
  pet: 'Pet',
  petstyle: 'Pet Style',
};

export interface ItemInfo {
  type: ItemType;
  id: string;
  name: string;
  rarity: Rarity;
  price: number;
  currency: Currency;
  unlockLevel: number;
  source?: string;
}

function contentItem(type: ItemType, id: string): Omit<ItemInfo, 'type' | 'id'> | null {
  switch (type) {
    case 'weapon': {
      const w = WEAPON_BY_ID[id];
      return w ? { name: w.name, rarity: w.rarity, price: w.price, currency: w.currency, unlockLevel: w.unlockLevel, source: w.price === 0 && w.unlockLevel <= 1 ? 'starter' : undefined } : null;
    }
    case 'gadget': {
      const g = GADGET_BY_ID[id];
      return g ? { name: g.name, rarity: 'common', price: g.price, currency: g.currency, unlockLevel: g.unlockLevel, source: g.price === 0 ? 'starter' : undefined } : null;
    }
    case 'skin': {
      const s = SKIN_PRESET_BY_ID[id];
      return s ? { name: s.name, rarity: s.rarity, price: s.price, currency: s.currency, unlockLevel: s.unlockLevel, source: s.source ?? (s.price === 0 && s.unlockLevel <= 1 ? 'starter' : undefined) } : null;
    }
    case 'armor': {
      const a = ARMOR_BY_ID[id];
      return a ? { name: a.name, rarity: a.rarity, price: a.price, currency: a.currency, unlockLevel: a.unlockLevel, source: a.source ?? (a.price === 0 ? 'starter' : undefined) } : null;
    }
    case 'camo': {
      const c = CAMO_BY_ID[id];
      return c ? { name: c.name, rarity: c.rarity, price: c.price, currency: c.currency, unlockLevel: c.unlockLevel, source: c.source } : null;
    }
    case 'tracer': {
      const t = TRACER_BY_ID[id];
      return t ? { name: t.name, rarity: t.rarity, price: t.price, currency: t.currency, unlockLevel: t.unlockLevel, source: t.source } : null;
    }
    case 'scope': {
      const sc = SCOPE_BY_ID[id];
      return sc ? { name: sc.name, rarity: sc.rarity, price: sc.price, currency: sc.currency, unlockLevel: sc.unlockLevel, source: sc.source } : null;
    }
    case 'deathfx': {
      const d = DEATH_FX_BY_ID[id];
      return d ? { name: d.name, rarity: d.rarity, price: d.price, currency: d.currency, unlockLevel: d.unlockLevel, source: d.source } : null;
    }
    case 'banner': {
      const b = BANNER_BY_ID[id];
      return b ? { name: b.name, rarity: b.rarity, price: b.price, currency: b.currency, unlockLevel: b.unlockLevel, source: b.source } : null;
    }
    case 'pet':
    case 'petstyle': {
      // Same derivation as the API catalog (pet rarity from the coin price, starter = free at level 1).
      const e = catalogContentItem(type, id);
      return e ? { name: e.name, rarity: e.rarity, price: e.price, currency: e.currency, unlockLevel: e.unlockLevel, source: e.source } : null;
    }
    default:
      return null;
  }
}

/** Item info combining the live catalog (prices can be tuned by live-ops) with content defaults. */
export function itemInfo(type: ItemType, id: string): ItemInfo {
  const c = catalogEntry(type, id);
  const base = contentItem(type, id);
  return {
    type,
    id,
    name: c?.name ?? base?.name ?? id.replace(/_/g, ' '),
    rarity: c?.rarity ?? base?.rarity ?? 'common',
    price: c?.price ?? base?.price ?? 0,
    currency: c?.currency ?? base?.currency ?? 'coins',
    unlockLevel: c?.unlockLevel ?? base?.unlockLevel ?? 1,
    source: c?.source ?? base?.source,
  };
}

/** Owned per the inventory, or a starter item every account has. */
export function ownsItem(type: ItemType, id: string): boolean {
  return owns(type, id) || itemInfo(type, id).source === 'starter';
}

export type CosmeticState = 'equipped' | 'owned' | 'buy' | 'locked' | 'unavailable';

/** Shop state of an item for the current player. */
export function shopState(type: ItemType, id: string, equipped: boolean): CosmeticState {
  if (equipped) return 'equipped';
  if (ownsItem(type, id)) return 'owned';
  const info = itemInfo(type, id);
  if (info.source && info.source !== 'shop' && info.source !== 'starter') return 'unavailable';
  if (level.value < info.unlockLevel) return 'locked';
  return 'buy';
}

export const SOURCE_LABEL: Record<string, string> = {
  battlepass: 'Battle Pass',
  achievement: 'Achievement',
  event: 'Event',
  mastery: 'Mastery',
  starter: 'Starter',
  shop: 'Store',
};

/** Icon name (ui/icons) for an item without a picture of its own (reward chips). */
export function itemIcon(type: ItemType, id: string): string {
  if (type === 'pet') return PET_ROLE[PET_BY_ID[id]?.role]?.icon ?? 'paw-print';
  if (type === 'petstyle') return 'paw-print';
  return 'gift';
}

export function rewardSummary(r: RewardBundle | null | undefined): string[] {
  if (!r) return [];
  const out: string[] = [];
  if (r.coins) out.push(`${r.coins.toLocaleString()} coins`);
  if (r.gems) out.push(`${r.gems.toLocaleString()} gems`);
  if (r.xp) out.push(`${r.xp.toLocaleString()} XP`);
  if (r.bpXp) out.push(`${r.bpXp.toLocaleString()} pass XP`);
  for (const it of r.items ?? []) out.push(itemInfo(it.type, it.id).name);
  return out;
}

// Pets ---------------------------------------------------------------------------------------------------------

export const PET_ROLE: Record<PetRole, { label: string; icon: string }> = {
  scout: { label: 'Scout', icon: 'bird' },
  medic: { label: 'Medic', icon: 'heart-pulse' },
  digger: { label: 'Digger', icon: 'pickaxe' },
  guard: { label: 'Guard', icon: 'shield' },
  attacker: { label: 'Attacker', icon: 'swords' },
};

/** The equipped pet ('' = none). */
export function equippedPet(): string {
  return loadout.value.pet ?? '';
}

/** Pet shown when previewing pet styles: the equipped one, else the starter pet. */
export function stylePreviewPet(): string {
  return equippedPet() || STARTER_LOADOUT.pet;
}

const secs = (v: number | undefined): string => `${v ?? 0} s`;

/** What a pet does, as short plain-words facts (armory detail panel). */
export function petFacts(p: PetDef): string[] {
  const out: string[] = [];
  switch (p.role) {
    case 'scout':
      out.push(`Spots enemies within ${p.range} m`, `Every ${secs(p.interval)}`, `Marks last ${secs(p.duration)}`);
      break;
    case 'medic':
      out.push(`Heals ${p.amount} HP a second`, `After ${secs(p.duration)} without getting hit`);
      break;
    case 'digger':
      out.push(`Digs where you shoot, up to ${p.range} m away`, `A hole every ${secs(p.interval)}`);
      break;
    case 'guard':
      out.push(`Takes ${Math.round((p.amount ?? 0) * 100)}% of the damage aimed at you`);
      break;
    case 'attacker':
      out.push(`${p.damage} damage per bite`, `Bites every ${secs(p.interval)}`, `Goes after enemies within ${p.range} m`);
      break;
  }
  out.push(`${p.hp} HP`, `Rebuilds in ${secs(p.reform)}`);
  return out;
}

// Weapon stats -------------------------------------------------------------------------------------------

export interface WeaponStats {
  damage: number;
  dps: number;
  fireRate: number;
  range: number;
  mobility: number;
  destruction: number;
}

function rawStats(w: WeaponDef): WeaponStats {
  const pellets = w.pellets ?? 1;
  const rps = w.rpm / 60;
  // Sustained shots per second including reloads.
  const srps = w.mag > 0 ? w.mag / (w.mag / rps + w.reload) : rps;
  let damage: number;
  let dps: number;
  let destruction: number;
  if (w.beam) {
    damage = w.beam.dps / Math.max(1, rps);
    dps = w.beam.dps;
    destruction = w.beam.voxelDps * (1 + w.beam.voxelRadius * 4);
  } else if (w.projectile) {
    const ex = w.projectile.explosion;
    damage = w.projectile.impactDamage + (ex ? ex.damage : 0) + (w.projectile.burn ?? 0) * 2;
    if (w.projectile.cluster) damage += w.projectile.cluster.count * (w.projectile.cluster.def.explosion?.damage ?? 0) * 0.5;
    dps = damage * srps;
    destruction = ex ? ex.voxelPower * ex.voxelRadius * ex.voxelRadius * 6 * Math.max(0.6, srps) : w.voxelPower * srps * 3;
  } else if (w.melee) {
    damage = w.damage;
    dps = damage * rps;
    destruction = w.melee.voxelPower * w.melee.voxelRadius * 10 * rps;
  } else {
    damage = w.damage * pellets;
    dps = damage * srps;
    destruction = w.voxelPower * pellets * srps * (1 + (w.voxelRadius ?? 0) * 10) * (1 + (w.penetration ?? 0) / 100);
  }
  const range = w.melee ? w.melee.range : w.beam ? w.beam.range : w.falloffEnd ? Math.min(w.range, w.falloffEnd * 1.4) : w.range;
  return { damage, dps, fireRate: w.rpm, range, mobility: w.moveSpeed, destruction };
}

const STAT_MAX = (() => {
  const m: WeaponStats = { damage: 1, dps: 1, fireRate: 1, range: 1, mobility: 1, destruction: 1 };
  for (const w of SHOP_WEAPONS) {
    const s = rawStats(w);
    for (const k of Object.keys(m) as (keyof WeaponStats)[]) m[k] = Math.max(m[k], s[k]);
  }
  return m;
})();

/** Raw numbers plus 0..1 bar values (sqrt-scaled so small differences stay visible). */
export function weaponStats(w: WeaponDef): { raw: WeaponStats; bars: WeaponStats } {
  const raw = rawStats(w);
  const bar = (k: keyof WeaponStats): number => Math.max(0.04, Math.min(1, Math.sqrt(raw[k] / STAT_MAX[k])));
  const mobility = Math.max(0.04, Math.min(1, (raw.mobility - 0.75) / (STAT_MAX.mobility - 0.75)));
  return { raw, bars: { damage: bar('damage'), dps: bar('dps'), fireRate: bar('fireRate'), range: bar('range'), mobility, destruction: bar('destruction') } };
}
